import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';

const BlogPostPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: '5 Ways to Reduce Construction Material Costs | Go Global Inc.',
    description: 'Proven strategies for optimizing your material procurement process and achieving significant cost savings on large projects without compromising quality.',
    keywords: 'reduce construction costs, material procurement, cost savings, construction budget, procurement strategies',
    canonical: `${siteUrl}/blog/reduce-construction-material-costs`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: '5 Ways to Reduce Construction Material Costs Without Compromising Quality',
      description: 'Proven strategies for optimizing your material procurement process and achieving significant cost savings on large projects without compromising quality.',
      author: {
        '@type': 'Person',
        name: 'Sarah Martinez',
      },
      datePublished: '2025-01-12',
      dateModified: '2025-01-12',
      publisher: {
        '@type': 'Organization',
        name: 'Go Global Inc.',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/favicon.ico`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/blog/reduce-construction-material-costs`,
      },
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        <section className="pt-32 pb-12 lg:pt-40 lg:pb-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <Link to="/blog" className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700 transition-colors mb-8 cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Blog
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-xs font-semibold text-primary-600 uppercase tracking-wide mb-4">
                Cost Management
              </div>
              <h1 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight mb-6">
                5 Ways to Reduce Construction Material Costs Without Compromising Quality
              </h1>
              <div className="flex items-center space-x-4 text-gray-600 mb-8">
                <span>Sarah Martinez</span>
                <span>•</span>
                <span>January 12, 2025</span>
                <span>•</span>
                <span>6 min read</span>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="pb-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="relative h-96 lg:h-[600px] rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/eaa928dd245a8ed59e1910825230737a.jpg"
                alt="Construction Material Cost Management"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </section>

        <section className="pb-20 lg:pb-28">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <article className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Material costs typically represent 40-50% of total construction project expenses. Finding ways to reduce these costs without sacrificing quality can dramatically improve project profitability and competitiveness. Here are five proven strategies that successful contractors use to optimize their procurement spending.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                1. Establish Direct Factory Relationships
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                One of the most effective ways to reduce material costs is by eliminating middlemen and purchasing directly from manufacturers. While this approach requires more upfront effort in vetting suppliers and managing logistics, the potential savings of 20-40% make it worthwhile for medium to large projects.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Direct factory relationships also provide greater control over product specifications, quality standards, and delivery schedules. Many overseas manufacturers are eager to work with international contractors and offer flexible minimum order quantities for established partners.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                2. Leverage Bulk Purchasing and Container Optimization
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Strategic bulk purchasing across multiple projects or in partnership with other contractors can unlock significant volume discounts. When sourcing internationally, optimizing container loads to maximize cubic footage utilization reduces per-unit shipping costs substantially.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Consider coordinating material orders across concurrent projects or establishing relationships with complementary contractors to share container space. A well-planned 40-foot container can accommodate materials for multiple projects while keeping freight costs per item at minimum levels.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                3. Implement Value Engineering Early
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Value engineering during the design phase identifies opportunities to specify materials that deliver equivalent performance at lower costs. This might involve selecting alternative products, adjusting dimensions to match standard sizes, or choosing different finishes that achieve similar aesthetic goals.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Engage suppliers early in the design process to understand which specifications drive costs and where flexibility exists. Often, minor adjustments to requirements can lead to substantial savings without affecting project outcomes or client satisfaction.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                4. Diversify Your Supplier Base Globally
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Relying on a single geographic region or limited supplier network leaves you vulnerable to price fluctuations and supply disruptions. Developing relationships with quality manufacturers in multiple countries creates competitive pressure and provides backup options when primary sources face challenges.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Different regions offer advantages for specific product categories. For example, China excels in hardware and fixtures, Vietnam offers competitive pricing on furniture, while Turkey and India provide excellent value for certain building materials. Understanding these regional strengths allows for optimized sourcing strategies.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                5. Invest in Professional Logistics Management
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                While it may seem counterintuitive to spend money to save money, partnering with experienced logistics professionals often pays for itself many times over. Expert freight forwarders and customs brokers prevent costly mistakes, identify the most efficient shipping routes, and ensure compliance with all regulations.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Professional logistics partners can also consolidate shipments, negotiate better carrier rates through their volume relationships, and provide valuable guidance on documentation requirements. The peace of mind and time savings alone justify the modest fees for their services.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Conclusion: A Strategic Approach to Cost Reduction
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Reducing material costs requires a comprehensive strategy that combines direct sourcing, smart purchasing practices, design optimization, supplier diversification, and professional logistics support. The contractors who master these approaches consistently deliver projects under budget while maintaining the quality standards their clients expect.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Start by implementing one or two of these strategies on your next project and gradually expand your capabilities as you gain experience. The investment in building these processes and relationships will pay dividends across all your future projects.
              </p>
            </article>

            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center text-white text-2xl font-semibold">
                  SM
                </div>
                <div>
                  <h3 className="font-display font-semibold text-xl text-gray-900 mb-2">Sarah Martinez</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Sarah is a construction procurement specialist with expertise in cost optimization and international sourcing. She has helped dozens of contractors reduce material expenses while improving quality control.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;